import React, { useState, useEffect, useRef } from 'react';
import type { MediaWidgetConfig } from './MediaWidget';
import { Upload } from 'lucide-react';

interface MediaWidgetConfigProps {
  initialConfig: Partial<MediaWidgetConfig>;
  onChange: (config: MediaWidgetConfig) => void;
}

export function MediaWidgetConfigForm({ initialConfig = {}, onChange }: MediaWidgetConfigProps) {
  const [sourceType, setSourceType] = useState<'upload' | 'url'>(initialConfig?.sourceType || 'url');
  const [url, setUrl] = useState(initialConfig?.url || '');
  const [mediaType, setMediaType] = useState<'image' | 'video'>(initialConfig?.mediaType || 'image');
  const [fit, setFit] = useState<'contain' | 'cover' | 'fill'>(initialConfig?.fit || 'contain');
  const [backgroundColor, setBackgroundColor] = useState(initialConfig?.backgroundColor || '');
  const [uploadError, setUploadError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Live update on every change
  useEffect(() => {
    const config: MediaWidgetConfig = {
      sourceType,
      url: url || undefined,
      mediaType,
      fit,
      backgroundColor: backgroundColor || undefined,
    };
    onChange(config);
  }, [sourceType, url, mediaType, fit, backgroundColor]);

  const handleFileUpload = (file: File) => {
    setUploadError('');

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validVideoTypes = ['video/mp4', 'video/webm'];
    const isImage = validImageTypes.includes(file.type);
    const isVideo = validVideoTypes.includes(file.type);

    if (!isImage && !isVideo) {
      setUploadError('Invalid file type. Use jpg, png, gif, webp, mp4, or webm');
      return;
    }

    // Validate file size
    const maxImageSize = 10 * 1024 * 1024; // 10MB
    const maxVideoSize = 100 * 1024 * 1024; // 100MB
    const maxSize = isImage ? maxImageSize : maxVideoSize;

    if (file.size > maxSize) {
      setUploadError(`File too large. Max ${isImage ? '10MB' : '100MB'} for ${isImage ? 'images' : 'videos'}`);
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      setUrl(base64);
      setSourceType('upload');
      setMediaType(isImage ? 'image' : 'video');
    };
    reader.onerror = () => {
      setUploadError('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleURLPaste = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pastedUrl = e.target.value;
    setUrl(pastedUrl);
    setSourceType('url');
    setUploadError('');

    // Auto-detect media type from URL extension
    const lowerUrl = pastedUrl.toLowerCase();
    if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
      setMediaType('image');
    } else if (lowerUrl.match(/\.(mp4|webm)$/)) {
      setMediaType('video');
    }
  };

  return (
    <div className="space-y-4 flex-1 overflow-auto">
      {/* Widget Background - Prominent */}
      <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Widget Background
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={backgroundColor || '#ffffff'}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="w-14 h-14 rounded-lg cursor-pointer border-2 border-gray-400 dark:border-gray-500"
            title="Widget background color"
          />
          <div className="flex-1">
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              placeholder="#ffffff"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-1"
              maxLength={7}
            />
            {backgroundColor && (
              <button
                onClick={() => setBackgroundColor('')}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Media Source - Smart Input */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Media Source *
        </label>

        {/* URL Input */}
        <input
          type="text"
          value={sourceType === 'url' ? url : ''}
          onChange={handleURLPaste}
          placeholder="Paste image or video URL..."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm mb-2"
        />

        {/* Upload Button */}
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all cursor-pointer"
        >
          <div className="flex flex-col items-center gap-2 text-gray-600 dark:text-gray-400">
            <Upload size={32} />
            <p className="text-sm font-semibold">Upload File</p>
            <p className="text-xs text-center">
              Click to browse or drag & drop
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 text-center">
              Images: 10MB max • Videos: 100MB max
            </p>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
          onChange={handleFileSelect}
          className="hidden"
        />

        {uploadError && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{uploadError}</p>
        )}
      </div>

      {/* Fit */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Fit
        </label>
        <select
          value={fit}
          onChange={(e) => setFit(e.target.value as 'contain' | 'cover' | 'fill')}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value="contain">Contain (fit within, maintain ratio)</option>
          <option value="cover">Cover (fill widget, may crop)</option>
          <option value="fill">Fill (stretch to fill)</option>
        </select>
      </div>
    </div>
  );
}
